// components/EmailComposer.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Chip,
  FormControl,
  FormHelperText,
  Box,
  CircularProgress,
  OutlinedInput,
  InputAdornment,
  Select,
  MenuItem,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import Autocomplete from "@mui/material/Autocomplete";
import { FrolaEditor } from "../../../../components/common";
import { makeStyles } from "@material-ui/core/styles";
import { contactsTypesActions } from "../../../../store/contactTypes/contactTypesActions";
import { emailTemplateActions } from "../../../../store/emailTemplate/emailTemplateActions";
import { useDispatch } from "react-redux";

const useStyles = makeStyles(theme => ({
  featuredImageTabs: {
    backgroundColor: "#ffffff",
    zIndex: "5",
    boxShadow: "0px 0px 6px rgba(0, 0, 0, 0.25)",
    borderRadius: "6px",
    maxHeight: "calc(100% - 64px) !important",
    maxWidth: "750px",
    width: "100%",
    overflowY: "auto",
    padding: 0,
    paddingTop: "0 !important",
  },
  featuredImageTabsHeading: {
    fontSize: "22px",
    color: "#fff",
    padding: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#3699FF",
    marginTop: "0",
  },
  closeTabs: {
    cursor: "pointer",
    color: "#fff",
  },
  form: {
    padding: "0px 15px",
  },
  templateDropdown: {
    display: "flex",
    alignItems: "center",
    marginBottom: "15px",
    border: "1px solid rgba(0, 0, 0, 0.23)",
    borderRadius: "4px",
    padding: "0 14px",
    height: "40px",
    position: "relative",
    marginTop: "15px",
  },
  templateLabel: {
    marginRight: "8px",
    color: "rgba(0, 0, 0, 0.54)",
    fontSize: "14px",
    whiteSpace: "nowrap",
  },

  templateSelect: {
    flex: 1,
    fontSize: "14px",
    background: "transparent",
    // Remove the underline and other pseudo-element borders
    "&:before, &:after": {
      display: "none",
    },
    // Target the input element directly and override its border
    "& .MuiInputBase-input": {
      border: "none !important", // This overrides the global !important rule
    },
    // Optionally adjust padding to maintain alignment
    padding: "8px 0", // match container height
  },
  emailInput: {
    marginTop: "10px",
    width: "100%",
    "& .MuiInputBase-input": {
      border: "unset !important",
    },
  },
  subjectInput: {
    width: "100%",
    marginTop: "20px",
    "& .MuiInputBase-input": {
      border: "unset !important",
    },
  },
  autocompleteContainer: {
    position: "relative",
    marginTop: "10px",
  },
  selectAllCheckbox: {
    position: "absolute",
    left: "4px",
    top: "5px",
    zIndex: 1,
  },
  checkBoxInput: {
    width: "20px",
    height: "20px",
  },
  errorText: {
    color: "#f44336 !important",
    marginTop: "4px",
    fontSize: "0.75rem",
  },
  helperText: {
    marginTop: "4px",
    color: "rgba(0, 0, 0, 0.6)",
    fontSize: "0.75rem",
  },
  tagsHeadings: {
    color: "#2d3339",
    fontSize: "14px",
    fontWeight: "500",
    marginTop: "10px",
    marginBottom: "5px",
  },
  chip: {
    margin: theme.spacing(0.5),
    backgroundColor: "#F3F6F9",
    color: "#70808F",
    fontFamily: "Roboto",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "145%",
  },
  tagBox: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "start",
    marginBottom: "10px",
    maxHeight: "40px",
    overflowY: "auto",
  },
  notFoundText: {
    textAlign: "center",
    opacity: 0.5,
    fontSize: "12px",
    fontWeight: 500,
    marginBottom: "10px",
  },
  filterAutoComplete: {
    width: "100%",
    "& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input": {
      padding: "7.5px 4px 7.5px 5px !important",
      marginLeft: "25px !important",
      border: "0px !important",
    },
  },
  composerActions: {
    padding: "16px 24px",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
}));

const EmailComposer = ({
  open,
  onClose,
  initialData,
  mode,
  onSend,
  selectedEmail,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [prospects, setProspects] = useState([]);
  const [selectedProspects, setSelectedProspects] = useState([]);
  const [to, setTo] = useState(""); // Only for manual emails
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("<p><br></p>");
  const [sending, setSending] = useState(false);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (open) {
      fetchEmailTemplates();
    }
  }, [open]);

  // Load prospects when dialog opens
  useEffect(() => {
    if (open) {
      loadProspects();
    }
  }, [open]);

  useEffect(() => {
    if (open && initialData) {
      // For reply/forward, prefill the To field with the initial email
      if (mode === "reply" || mode === "replyAll" || mode === "forward") {
        setTo(initialData.to || "");
      } else {
        setTo("");
      }
      setSubject(initialData.subject || "");
      setBody(initialData.body || "<p><br></p>");
      setAttachments([]);
      setErrors({});

      // Reset selected prospects when opening with initial data
      setSelectedProspects([]);
      setSelectAll(false);
    }
  }, [open, initialData, mode]);

  // Update selectAll checkbox when prospects or selection changes
  useEffect(() => {
    if (prospects.length > 0) {
      setSelectAll(prospects.length === selectedProspects.length);
    }
  }, [prospects, selectedProspects]);

  // ADDED: Function to fetch email templates
  const fetchEmailTemplates = useCallback(async () => {
    try {
      const result = await dispatch(
        emailTemplateActions.onGetTemplatePaginatedData({
          pagination: {
            pageSize: 1000,
            pageNo: 1,
            orderByKey: "Id",
            orderByVal: 0,
          },
          templateName: "",
          subject: "",
          body: "",
          search: "",
          DateFrom: null,
          DateTo: null,
        }),
      );
      if (result?.data?.success) {
        setEmailTemplates(result.data?.data?.data);
      }
    } catch (error) {
      console.error("Error fetching email templates:", error);
    }
  }, [dispatch]);

  // ADDED: Handle template selection
  const handleTemplateSelect = templateId => {
    if (!templateId) {
      // Clear form when "Select Template" is chosen
      setSelectedTemplateId("");
      setSubject("");
      setBody("<p><br></p>");
      return;
    }
    const selected = emailTemplates.find(t => t.id === templateId);
    if (selected) {
      setSelectedTemplateId(templateId);
      setSubject(selected.subject);
      setBody(selected.body);
    }
  };

  // Function to load prospects from API
  const loadProspects = async () => {
    try {
      setLoadingProspects(true);

      // TODO: Replace with your actual API call
      // Example API response structure:
      const response = await dispatch(
        contactsTypesActions.onGetAllProspectsList(),
      );
      if (response?.success) {
        setProspects(response?.data);
      } else {
        setProspects([]);
      }
      setLoadingProspects(false);
    } catch (error) {
      console.error("Error loading prospects:", error);
      setLoadingProspects(false);
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case "reply":
        return "Reply";
      case "replyAll":
        return "Reply All";
      case "forward":
        return "Forward";
      default:
        return "New Message";
    }
  };

  const handleSelectAll = event => {
    const checked = event.target.checked;
    setSelectAll(checked);

    if (checked) {
      setSelectedProspects([...prospects]);
    } else {
      // When unchecking, remove all selected prospects
      setSelectedProspects([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Get emails from selected prospects
    const prospectEmails = selectedProspects.map(p => p.email);

    // Get manual emails from To field
    const manualEmails = to
      .split(",")
      .map(email => email.trim())
      .filter(email => email);

    // Combine all emails
    const allEmails = [...prospectEmails, ...manualEmails];

    // Validation: Either prospect selected OR manual emails entered
    if (allEmails.length === 0) {
      newErrors.to =
        "Please select at least one prospect or enter email addresses";
    } else {
      // Validate email format for all emails
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (const email of allEmails) {
        if (!emailRegex.test(email)) {
          newErrors.to = `Invalid email format: ${email}`;
          break;
        }
      }
    }

    if (!subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSending(true);

      // Get emails from selected prospects
      const prospectEmails = selectedProspects.map(p => p.email);

      // Get manual emails from To field
      const manualEmails = to
        .split(",")
        .map(email => email.trim())
        .filter(email => email);

      // Combine all emails (prospect emails + manual emails)
      const allEmails = [...prospectEmails, ...manualEmails];
      const uniqueEmails = [...new Set(allEmails)]; // Remove duplicates

      const emailData = {
        messageId:
          mode !== "new" && selectedEmail?.messageId
            ? selectedEmail.messageId
            : 0,
        body: body || "<p><br></p>",
        subject: subject,
        toEmails: uniqueEmails, // Send combined emails
        replyAll: mode === "replyAll" ? true : false,
        // Include prospect IDs if needed
        prospectIds: selectedProspects.map(p => p.id),
        // Include attachments if needed
        attachments: attachments.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
        })),
      };

      console.log("Sending email with data:", emailData);
      const success = await onSend(emailData);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Failed to send email: " + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleToChange = e => {
    const value = e.target.value;
    setTo(value);

    if (errors.to) {
      setErrors({ ...errors, to: null });
    }
  };

  const handleDeleteProspect = id => {
    const prospectToRemove = selectedProspects.find(p => p.id === id);
    if (!prospectToRemove) return;

    // Remove the prospect from selected prospects
    const newSelectedProspects = selectedProspects.filter(
      prospect => prospect.id !== id,
    );
    setSelectedProspects(newSelectedProspects);
  };

  // Handle file upload for attachments
  const handleFileUpload = e => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  // Remove attachment
  const removeAttachment = index => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  function truncate(text) {
    if (text && text.length > 40) {
      return text.substring(0, 38) + "...";
    }
    return text;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        className: classes.featuredImageTabs,
      }}>
      <div className={classes.featuredImageTabsHeading}>
        {getDialogTitle()}
        <IconButton
          onClick={onClose}
          className={classes.closeTabs}
          size='small'
          disabled={sending}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.form}>
        {/* ADDED: Email Template Dropdown */}
        {emailTemplates.length > 0 && (
          <div className={classes.templateDropdown}>
            <span className={classes.templateLabel}>Email Template :</span>
            <Select
              value={selectedTemplateId}
              onChange={e => handleTemplateSelect(e.target.value)}
              displayEmpty
              variant='standard'
              disableUnderline
              className={classes.templateSelect}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}>
              <MenuItem value=''>
                <em>Select Template</em>
              </MenuItem>
              {emailTemplates.map(template => (
                <MenuItem key={template.id} value={template.id}>
                  {template.templateName}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        {/* Prospect Autocomplete with Select All */}
        <div
          className={`${classes.autocompleteContainer} ${classes.filterAutoComplete}`}>
          <Autocomplete
            multiple
            options={prospects}
            getOptionLabel={option =>
              truncate(`${option.email} - ${option.firstName}`) || ""
            }
            value={selectedProspects}
            onChange={(event, newValue) => {
              setSelectedProspects(newValue);
            }}
            filterSelectedOptions
            loading={loadingProspects}
            disabled={sending}
            renderInput={params => (
              <TextField
                {...params}
                variant='outlined'
                placeholder='Search prospects...'
                fullWidth
              />
            )}
            // REMOVE renderTags to NOT show chips inside autocomplete
            renderTags={() => null} // This hides chips inside the input
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText='No prospects found'
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(
                option =>
                  option.firstName.toLowerCase().includes(searchTerm) ||
                  option.email.toLowerCase().includes(searchTerm),
              );
            }}
          />

          {/* Select All Checkbox */}
          {prospects.length > 0 && (
            <div className={`${classes.selectAllCheckbox} dateCheckbox`}>
              <input
                type='checkbox'
                className={classes.checkBoxInput}
                checked={selectAll}
                onChange={handleSelectAll}
                disabled={sending}
                title='Select All Prospects'
              />
            </div>
          )}
        </div>

        {/* Selected Prospects Display BELOW autocomplete */}
        {selectedProspects.length > 0 && (
          <div>
            <h2 className={classes.tagsHeadings}>Selected Prospects</h2>
            <div className={classes.tagBox}>
              {selectedProspects.map((prospect, index) => (
                <Chip
                  key={index}
                  label={truncate(prospect.firstName || prospect.email)}
                  onDelete={() => handleDeleteProspect(prospect?.id)}
                  className={classes.chip}
                />
              ))}
            </div>
          </div>
        )}

        {/* To Field - Only for manual email entry */}
        <div className='recipient-fields'>
          <div className={classes.recipientRow}>
            <span className={classes.recipientLabel}>To</span>
            <FormControl
              fullWidth
              error={!!errors.to}
              className={classes.emailInput}>
              <OutlinedInput
                fullWidth
                value={to}
                onChange={handleToChange}
                variant='outlined'
                size='small'
                placeholder='Enter email addresses (comma separated)'
                disabled={sending}
                error={!!errors.to}
                startAdornment={
                  <InputAdornment position='start'>Email : </InputAdornment>
                }
              />
              {errors.to && (
                <FormHelperText className={classes.errorText}>
                  {errors.to}
                </FormHelperText>
              )}
              {!errors.to && (
                <FormHelperText className={classes.helperText}>
                  Enter email addresses manually or select prospects above
                </FormHelperText>
              )}
            </FormControl>
          </div>
        </div>

        {/* Subject Field */}
        <FormControl
          fullWidth
          error={!!errors.subject}
          className={classes.subjectInput}>
          <OutlinedInput
            fullWidth
            value={subject}
            onChange={e => {
              setSubject(e.target.value);
              if (errors.subject) setErrors({ ...errors, subject: null });
            }}
            variant='outlined'
            required
            disabled={sending}
            error={!!errors.subject}
            placeholder='Subject'
            startAdornment={
              <InputAdornment position='start'>Subject : </InputAdornment>
            }
          />
          {errors.subject && (
            <FormHelperText className={classes.errorText}>
              {errors.subject}
            </FormHelperText>
          )}
        </FormControl>

        {/* Body Editor */}
        <FormControl fullWidth margin='normal'>
          <div className='email-body-editor'>
            <FrolaEditor
              model={body}
              onHandleModelChange={model => {
                setBody(model);
              }}
              placeholder='Type your message here (optional)...'
              maxWidth='100%'
              height={300}
              disabled={sending}
            />
            <FormHelperText className={classes.helperText}>
              Message body is optional - you can also send just attachments
            </FormHelperText>
          </div>
        </FormControl>

        {/* Attachments Section */}
        <div className={classes.attachmentsSection}>
          {attachments.length > 0 && (
            <div className={classes.attachmentsList}>
              {attachments.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name}
                  onDelete={() => removeAttachment(index)}
                  icon={<AttachFileIcon />}
                  size='small'
                  className={classes.attachmentChip}
                  disabled={sending}
                />
              ))}
            </div>
          )}
          {/* <Button
            variant='outlined'
            component='label'
            startIcon={<AttachFileIcon />}
            size='small'
            style={{ textTransform: "none", marginTop: "10px" }}
            disabled={sending}>
            Attach files
            <input
              type='file'
              multiple
              hidden
              onChange={handleFileUpload}
              disabled={sending}
            />
          </Button> */}
        </div>
      </DialogContent>

      <DialogActions className={classes.composerActions}>
        <Button
          onClick={onClose}
          style={{ textTransform: "none" }}
          disabled={sending}>
          Discard
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSend}
          disabled={sending}
          startIcon={<SendIcon />}
          style={{ textTransform: "none" }}>
          {sending ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailComposer;
